import { ApolloError, useMutation, useQuery } from '@apollo/client'
import { notification, Skeleton } from 'antd'
import { isNil } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  FIELD_CARBON_EMISSIONS,
  FieldCarbonEmissions,
  FieldCarbonEmissionsVariables,
  UPDATE_FIELD_CARBON_CONSUMPTION,
  UpdateFieldCarbonConsumption,
  UpdateFieldCarbonConsumptionVariables,
} from 'api'
import { MetricValue } from 'features/MainRouter/features/DasboardRouter/features/components'
import { GraphQLUtils } from 'utils'

import { Card } from '../Card'
import {
  ConsumptionColumn,
  ConsumptionInput,
  ConsumptionItem,
  ConsumptionLabel,
  ConsumptionMetric,
  ConsumptionUnit,
  Container,
  Content,
  EmissionsColumn,
  EmissionsList,
  Label,
  MetricContainer,
  MetricRow,
  Title,
} from './components'

interface Props {
  canUpdateConsumption: boolean
  fieldId: number
  seasonId: number
}

type ConsumptionField = 'electricityConsumptionKwh' | 'fuelConsumptionLiters'
type ConsumptionFlags = Record<ConsumptionField, boolean>
type ConsumptionCounters = Record<ConsumptionField, number>

const EMPTY_CONSUMPTION_FLAGS: ConsumptionFlags = {
  electricityConsumptionKwh: false,
  fuelConsumptionLiters: false,
}

const EMPTY_CONSUMPTION_COUNTERS: ConsumptionCounters = {
  electricityConsumptionKwh: 0,
  fuelConsumptionLiters: 0,
}

function formatConsumptionValue(value: number | null): string {
  return isNil(value) ? '' : String(value)
}

function parseConsumptionValue(value: string, locale: string): number | null | undefined {
  const decimalSeparator = Intl.NumberFormat(locale)
    .formatToParts(1.1)
    .find(({ type }) => type === 'decimal')?.value
  const normalizedValue = value.trim().replace(decimalSeparator ?? '.', '.')
  if (!normalizedValue) return null

  const parsedValue = Number(normalizedValue)
  if (!Number.isFinite(parsedValue) || parsedValue < 0) return undefined

  return parsedValue
}

export const FieldCarbonEmissionsCard: React.FC<Props> = ({
  canUpdateConsumption,
  fieldId,
  seasonId,
}) => {
  const { i18n, t } = useTranslation('dashboard')
  const { t: commonT } = useTranslation('common')
  const [invalidConsumptionFields, setInvalidConsumptionFields] =
    useState<ConsumptionFlags>(EMPTY_CONSUMPTION_FLAGS)
  const [inputResetCounters, setInputResetCounters] = useState<ConsumptionCounters>(
    EMPTY_CONSUMPTION_COUNTERS,
  )
  const [isSavingConsumption, setIsSavingConsumption] = useState(false)
  const { data, loading, refetch } = useQuery<FieldCarbonEmissions, FieldCarbonEmissionsVariables>(
    FIELD_CARBON_EMISSIONS,
    {
      variables: { fieldId, seasonId },
      fetchPolicy: 'network-only',
      onError: GraphQLUtils.errorHandler,
    },
  )
  const [updateFieldCarbonConsumption] = useMutation<
    UpdateFieldCarbonConsumption,
    UpdateFieldCarbonConsumptionVariables
  >(UPDATE_FIELD_CARBON_CONSUMPTION)

  const emissions = data?.field.carbonEmissions

  const resetConsumptionInput = (field: ConsumptionField) => {
    setInputResetCounters(currentCounters => ({
      ...currentCounters,
      [field]: currentCounters[field] + 1,
    }))
    setInvalidConsumptionFields(currentFields => ({ ...currentFields, [field]: false }))
  }

  const handleConsumptionBlur = async (field: ConsumptionField, value: string) => {
    const parsedConsumption = parseConsumptionValue(value, i18n.resolvedLanguage ?? i18n.language)
    setInvalidConsumptionFields(currentFields => ({
      ...currentFields,
      [field]: parsedConsumption === undefined,
    }))
    if (parsedConsumption === undefined) return

    const confirmedConsumption = emissions?.[field] ?? null
    if (parsedConsumption === confirmedConsumption) return

    setIsSavingConsumption(true)
    try {
      const result = await updateFieldCarbonConsumption({
        variables: { fieldId, seasonId, [field]: parsedConsumption },
      })
      if (!result.data?.updateFieldCarbonConsumption.success) {
        resetConsumptionInput(field)
        notification.error({ message: commonT('messages.genericErrorTryAgain') })
        return
      }
      try {
        await refetch()
      } catch (error) {
        if (error instanceof ApolloError) GraphQLUtils.errorHandler(error)
      }
    } catch (error) {
      if (error instanceof ApolloError) GraphQLUtils.errorHandler(error)
      resetConsumptionInput(field)
    } finally {
      setIsSavingConsumption(false)
    }
  }

  const formatValue = (value?: number | null) => {
    if (isNil(value)) return undefined
    return commonT('systemsFormat.localizeNumber', { val: Math.round(value) })
  }

  const consumptionItems: Array<{
    field: ConsumptionField
    label: string
    unit: string
  }> = [
    {
      field: 'fuelConsumptionLiters',
      label: t('carbonEmissionsCard.fuelConsumption'),
      unit: commonT('systems.litersAbbreviation'),
    },
    {
      field: 'electricityConsumptionKwh',
      label: t('carbonEmissionsCard.electricityConsumption'),
      unit: commonT('systems.kilowattHoursAbbreviation'),
    },
  ]
  const emissionItems = [
    { label: t('carbonEmissionsCard.methane'), value: emissions?.methaneCo2eKg },
    { label: t('carbonEmissionsCard.nitrousOxide'), value: emissions?.nitrousOxideCo2eKg },
    { label: t('carbonEmissionsCard.carbonDioxide'), value: emissions?.carbonDioxideCo2eKg },
  ]
  const carbonDioxideEquivalentUnit = commonT('systems.carbonDioxideEquivalentKilograms')

  return (
    <Card height="208px" width="503px">
      <Skeleton active loading={loading} paragraph={{ rows: 3 }} title={false}>
        <Container>
          <Title level={5}>{t('carbonEmissionsCard.title')}</Title>
          <Content>
            <ConsumptionColumn>
              {consumptionItems.map(({ field, label, unit }) => (
                <ConsumptionItem key={field}>
                  <ConsumptionLabel>{label}</ConsumptionLabel>
                  {canUpdateConsumption ? (
                    <ConsumptionMetric>
                      <ConsumptionInput
                        key={inputResetCounters[field]}
                        aria-label={label}
                        aria-invalid={invalidConsumptionFields[field]}
                        defaultValue={formatConsumptionValue(emissions?.[field] ?? null)}
                        disabled={isSavingConsumption}
                        inputMode="decimal"
                        onBlur={event => handleConsumptionBlur(field, event.currentTarget.value)}
                        onChange={() =>
                          setInvalidConsumptionFields(currentFields => ({
                            ...currentFields,
                            [field]: false,
                          }))
                        }
                        onPressEnter={event => event.currentTarget.blur()}
                        placeholder="-"
                        status={invalidConsumptionFields[field] ? 'error' : undefined}
                      />
                      <ConsumptionUnit>{unit}</ConsumptionUnit>
                    </ConsumptionMetric>
                  ) : (
                    <MetricValue
                      value={formatValue(emissions?.[field])}
                      unit={unit}
                      valueWidth={70}
                      variant="compact"
                    />
                  )}
                </ConsumptionItem>
              ))}
            </ConsumptionColumn>
            <EmissionsColumn>
              <EmissionsList>
                {emissionItems.map(({ label, value }) => (
                  <MetricRow key={label}>
                    <Label>{label}</Label>
                    <MetricContainer>
                      <MetricValue
                        size="small"
                        value={formatValue(value)}
                        valueWidth={60}
                        unit={carbonDioxideEquivalentUnit}
                        variant="compact"
                      />
                    </MetricContainer>
                  </MetricRow>
                ))}
              </EmissionsList>
              <MetricRow>
                <Label>{t('carbonEmissionsCard.total')}</Label>
                <MetricContainer>
                  <MetricValue
                    size="small"
                    value={formatValue(emissions?.totalCo2eKg)}
                    valueWidth={60}
                    unit={carbonDioxideEquivalentUnit}
                    variant="compact"
                  />
                </MetricContainer>
              </MetricRow>
            </EmissionsColumn>
          </Content>
        </Container>
      </Skeleton>
    </Card>
  )
}
